import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import httpClient from "../../utils/httpClients";

function Podcast() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false); // New state for view modal
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [editEpisode, setEditEpisode] = useState(null);
  const [editEpisodeErrors, setEditEpisodeErrors] = useState({});
  const [viewingPodcast, setViewingPodcast] = useState(null); // New state for viewing podcast
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPodcastDetails, setLoadingPodcastDetails] = useState(false); // Loading for view modal
  const [songErrors, setSongErrors] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    artist: "",
    genre: "",
    releaseDate: "",
  });
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    artist: "",
    genre: "",
    releaseDate: "",
    episodes: [],
  });
  const [episodeForm, setEpisodeForm] = useState({
    title: "",
    duration: "",
    audioFile: null,
    description: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [uploadFormErrors, setUploadFormErrors] = useState({
    coverImage: "",
    file: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [podcastAudioFile, setPodcastAudioFile] = useState(null);
  const [editCoverImageFile, setEditCoverImageFile] = useState(null);
  const [editPodcastAudioFile, setEditPodcastAudioFile] = useState(null);
  const [genres, setGenres] = useState([]);
  const [loadingGenres, setLoadingGenres] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Add Episode dialog (popup to add episodes to a podcast)
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditEpisodeDialog, setShowEditEpisodeDialog] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null); // podcast selected for adding episodes
  const [songs, setSongs] = useState([]); // episode rows in the dialog
  const [uploading, setUploading] = useState(false);

  const [artistIds, setArtistIds] = useState([]);
  // Fetch podcasts from API
  const fetchPodcasts = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const response = await httpClient.get(
        `admin/media-collection/artist-get-all?page=${page}&limit=${limit}&contentType=podcast${
          search ? `&artist=${search}` : ""
        }`,
      );
      const allCollections = response.data.data || [];

      // Filter for podcasts only and transform data
      const podcastCollections = allCollections
        .filter((item) => item.contentType === "podcast")
        .map((podcast) => ({
          id: podcast._id,
          title: podcast.title || "",
          description: podcast.description || "",
          artist: podcast.artist || "",
          genre: podcast.genre || podcast.themes?.[0] || "",
          releaseDate: podcast.releaseDate?.split("T")[0] || "",
          coverImage: podcast.coverImage || null,
          coverImageUrl: podcast.coverImageUrl || "",
          numberOfItems: podcast.numberOfItems || [],
          status: podcast.status || "pending",
          playCount: podcast.playCount || 0,
          duration: podcast.duration || 0,
          file: podcast.file || null,
          fileUrl: podcast.fileUrl || "",
        }));

      setPodcasts(podcastCollections);

      // Calculate pagination - use totalCount or total from API response
      const totalItems =
        response.data.totalCount ||
        response.data.total ||
        podcastCollections.length;
      setTotalPages(Math.ceil(totalItems / limit) || 1);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
      setPodcasts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch podcast details by ID
  const fetchPodcastDetails = async (podcastId) => {
    console.log("ID BEFORE API CALL:", podcastId);

    if (!podcastId) {
      console.error("PodcastId is undefined before hitting API");
      return;
    }

    try {
      setLoadingPodcastDetails(true);
      const response = await httpClient.get(
        `admin/media-collection/get-by-id/${podcastId}`,
      );
      console.log("Received podcastId:", podcastId);

      const data = response.data;
      console.log("eposdie", data);
      if (data.success) {
        return data.data;
      } else {
        throw new Error(data.message || "Failed to fetch podcast details");
      }
    } catch (error) {
      console.error("Error fetching podcast details:", error);
      throw error;
    } finally {
      setLoadingPodcastDetails(false);
    }
  };

  // Fetch genres
  const fetchGenres = async () => {
    try {
      setLoadingGenres(true);
      const response = await httpClient.get("admin/genre/get-all-genre");
      setGenres(response.data.data?.getGenre || []);
    } catch (error) {
      console.error("Error fetching genres:", error);
      setGenres([]);
    } finally {
      setLoadingGenres(false);
    }
  };

  // Initialize with API data
  useEffect(() => {
    fetchGenres();
    fetchPodcasts();
  }, [currentPage, searchQuery]);

  // Handle view podcast
  const handleViewPodcast = async (podcast) => {
    try {
      const podcastDetails = await fetchPodcastDetails(podcast.id);
      // get-by-id returns data as array of episodes, or object with episodes
      const episodes = Array.isArray(podcastDetails)
        ? podcastDetails
        : (podcastDetails?.episodes ?? []);
      setViewingPodcast({
        ...podcast,
        details: {
          ...(typeof podcastDetails === "object" &&
          !Array.isArray(podcastDetails)
            ? podcastDetails
            : {}),
          episodes,
        },
      });
      setSelectedAlbum(podcast);

      setIsViewModalOpen(true);
    } catch (error) {
      console.error("Error loading podcast details:", error);
      // Fallback to basic data if API fails
      setViewingPodcast(podcast);
      setIsViewModalOpen(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (editFormErrors[name]) {
      setEditFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleEpisodeInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "audioFile") {
      setEpisodeForm((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setEpisodeForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = "Podcast title is required";
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
    }

    // if (!formData.artist.trim()) {
    //   errors.artist = "Artist is required";
    // }

    if (!formData.releaseDate) {
      errors.releaseDate = "Release date is required";
    }

    if (!formData.genre?.trim()) {
      errors.genre = "Podcast genre is required";
    }

    if (!coverImageFile) {
      errors.coverImage = "Cover image is required";
    }

    if (!podcastAudioFile) {
      errors.audioFile = "Podcast audio file is required";
    }

    setFormErrors(errors);
    setUploadFormErrors((prev) => ({
      coverImage: errors.coverImage || prev.coverImage,
      file: errors.audioFile || prev.file,
    }));
    return Object.keys(errors).length === 0;
  };

  const validateEditForm = () => {
    const errors = {};
    if (!editFormData.title?.trim()) errors.title = "Podcast title is required";
    if (!editFormData.description?.trim())
      errors.description = "Description is required";
    // if (!editFormData.artist?.trim()) errors.artist = "Artist is required";
    if (!editFormData.releaseDate)
      errors.releaseDate = "Release date is required";
    if (!editFormData.genre) errors.genre = "Podcast genre is required";
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      artist: "",
      genre: "",
      releaseDate: "",
    });
    setCoverImageFile(null);
    setPodcastAudioFile(null);
    setFormErrors({});
    setUploadFormErrors({ coverImage: "", file: "" });
  };

  const resetEpisodeForm = () => {
    setEpisodeForm({
      title: "",
      duration: "",
      audioFile: null,
      description: "",
    });
  };

  // API: Create new podcast
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const uploadFormData = new FormData();

      // Append required fields for podcast
      uploadFormData.append("contentType", "podcast");
      uploadFormData.append("title", formData.title);
      uploadFormData.append("description", formData.description);
      // uploadFormData.append("artist", formData.artist);
      uploadFormData.append("genre", formData.genre);
      uploadFormData.append("releaseDate", formData.releaseDate);
      uploadFormData.append("isFeatured", "true");
      uploadFormData.append("isActive", "true");
      uploadFormData.append("artistIds", artistIds);

      // Append files
      if (coverImageFile) {
        uploadFormData.append("coverImage", coverImageFile);
      }

      if (podcastAudioFile) {
        uploadFormData.append("file", podcastAudioFile);
      }

      const response = await httpClient.post(
        "admin/media-collection/add-collection",
        uploadFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        resetForm();
        setIsModalOpen(false);
        await fetchPodcasts(); // Refresh the list
      } else {
        console.error("Failed to create podcast:", response.data.message);
      }
    } catch (error) {
      console.error("Error creating podcast:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // API: Update podcast with episodes
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    setSubmitting(true);

    try {
      const updateFormData = new FormData();

      updateFormData.append("title", editFormData.title || "");
      updateFormData.append("description", editFormData.description || "");
      updateFormData.append("releaseDate", editFormData.releaseDate);
      updateFormData.append("artistIds", artistIds);

      updateFormData.append(
        "isFeatured",
        String(editFormData.isFeatured || "true"),
      );
      updateFormData.append(
        "isActive",
        String(editFormData.isActive || "true"),
      );

      if (editFormData.genre && Array.isArray(editFormData.genre)) {
        editFormData.genre.forEach((g) => updateFormData.append("genre", g));
      } else if (editFormData.genre) {
        updateFormData.append("genre", editFormData.genre);
      }

      if (editFormData.themes && Array.isArray(editFormData.themes)) {
        editFormData.themes.forEach((t) => updateFormData.append("themes", t));
      }

      if (editCoverImageFile) {
        updateFormData.append("coverImage", editCoverImageFile);
      }
      if (editPodcastAudioFile) {
        updateFormData.append("file", editPodcastAudioFile);
      }

      if (editFormData.episodes && editFormData.episodes.length > 0) {
        updateFormData.append(
          "episodes",
          JSON.stringify(editFormData.episodes),
        );
      }

      const response = await httpClient.put(
        `admin/media-collection/update/${editingPodcast.id}`,
        updateFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      console.log("Editing Podcast Data:", editingPodcast);

      if (response.data.success) {
        setIsEditModalOpen(false);
        setEditingPodcast(null);
        setEditCoverImageFile(null);
        // await fetchPodcastDetails();
        await fetchPodcasts(currentPage, searchTerm);
      }
    } catch (error) {
      console.error("Error updating podcast:", error);
      if (error.response) {
        console.log("Backend Error Message:", error.response.data.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateSongs = () => {
    const newErrors = songs.map((song) => {
      let errors = {};
      if (!(song.title || "").trim()) errors.title = "Title is required.";
      if (!(song.title || "").length > 100)
        errors.title =
          "Episode title should be maximum 100 characters is required";
      if (!(song.description || "").trim())
        errors.description = "Description is required.";
      if (!song.releaseDate) errors.releaseDate = "Please select Date.";
      if (!song.themes || song.themes.length === 0)
        errors.themes = "Plese select a Genre.";
      if (!song.coverImage) errors.coverImage = "Please select Cover image.";
      if (!song.file) errors.file = "Please select Audio file.";
      return errors;
    });

    setSongErrors([...newErrors]);

    const hasErrors = newErrors.some((err) => Object.keys(err).length > 0);
    return !hasErrors;
  };
  const handleSubmitSongs = async (e) => {
    e.preventDefault();
    if (!validateSongs()) {
      return;
    }
    setUploading(true);

    try {
      const uploadPromises = songs.map(async (song) => {
        const formData = new FormData();

        formData.append("contentType", "episode");
        formData.append("title", song.title);
        formData.append("parentId", selectedAlbum?.id || selectedAlbum?._id);
        formData.append("isAlbum", false);
        formData.append("artist", song.artist || selectedAlbum?.artist || "");
        formData.append("description", song.description || "");
        formData.append("releaseDate", song.releaseDate);
        formData.append("isActive", "true");
        formData.append("isFeatured", "false");
        formData.append("artistIds", artistIds);

        if (song.artistIds && song.artistIds.length > 0) {
          song.artistIds.forEach((artistId) => {
            formData.append("artistIds", artistId._id || artistId);
          });
        }

        if (song.themes && song.themes.length > 0) {
          song.themes.forEach((themeId) => {
            formData.append("genre", themeId);
          });
        }

        if (song.coverImage) {
          formData.append("coverImage", song.coverImage);
        }
        if (song.file) {
          formData.append("file", song.file);
        }

        return httpClient.post("admin/media-track/add-track", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      });

      await Promise.all(uploadPromises);
      setShowEditDialog(false);
      setSongs([]);
      setSelectedAlbum(null);
      await fetchPodcasts(currentPage, searchQuery);
      // Swal.fire("Success", "Episodes added successfully.", "success");
    } catch (error) {
      console.error("Error uploading episodes:", error);
      // Swal.fire("Error", "Failed to add episodes.", "error");
    } finally {
      setUploading(false);
    }
  };

  console.log("editEpisode", editEpisode);
  console.log("selectedAlbum", selectedAlbum);

  const validateEditEpisode = () => {
    let errors = {};
    if (!editEpisode.title?.trim()) errors.title = "Episode title is required.";
    if (!editEpisode.title?.length > 100)
      errors.title =
        "Episode title should be maximum 100 characters is required";
    if (!editEpisode.genre || editEpisode.genre.length === 0)
      errors.genre = "At least one genre is required.";

    if (!editEpisode.releaseDate)
      errors.releaseDate = "Release date is required.";

    if (!editEpisode.coverImageUrl)
      errors.coverImage = "Cover image is required.";
    if (!editEpisode.file && !editEpisode.fileUrl)
      errors.file = "Audio file is required.";

    setEditEpisodeErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleEditSubmitSongs = async (e) => {
    e.preventDefault();
    if (!validateEditEpisode()) {
      return;
    }
    setUploading(true);

    try {
      // const uploadPromises = songs.map(async (song) => {
      const formData = new FormData();

      formData.append("contentType", "episode");
      formData.append("title", editEpisode.title);
      formData.append("parentId", selectedAlbum?.id || selectedAlbum?._id);
      formData.append("isAlbum", false);
      formData.append("artistIds", artistIds);
      // formData.append(
      //   "artist",
      //   editEpisode.artist || selectedAlbum?.artist || "",
      // );
      formData.append("description", editEpisode.description || "");
      formData.append("releaseDate", editEpisode.releaseDate);
      formData.append("isActive", "true");
      formData.append("isFeatured", "false");

      if (editEpisode.artistIds && editEpisode.artistIds.length > 0) {
        editEpisode.artistIds.forEach((artistId) => {
          formData.append("artistIds", artistId._id || artistId);
        });
      }

      if (editEpisode.genre && editEpisode.genre.length > 0) {
        editEpisode.genre.forEach((themeId) => {
          formData.append("genre", themeId);
        });
      }

      if (editEpisode.coverImage) {
        formData.append("coverImage", editEpisode.coverImage);
      }
      if (editEpisode.file) {
        formData.append("file", editEpisode.file);
      }

      const res = await httpClient.put(
        `admin/media-track/update/${editEpisode?._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      // });

      // await Promise.all(uploadPromises);
      setShowEditEpisodeDialog(false);
      setEditEpisode(null);
      // await fetchPodcastDetails(selectedAlbum?.id);
      const podcastDetails = await fetchPodcastDetails(selectedAlbum?.id);
      // get-by-id returns data as array of episodes, or object with episodes
      const episodes = Array.isArray(podcastDetails)
        ? podcastDetails
        : (podcastDetails?.episodes ?? []);
      setViewingPodcast({
        ...selectedAlbum,
        details: {
          ...(typeof podcastDetails === "object" &&
          !Array.isArray(podcastDetails)
            ? podcastDetails
            : {}),
          episodes,
        },
      });
      // setSongs([]);
      // setSelectedAlbum(null);
      // await fetchPodcasts(currentPage, searchQuery);
      // Swal.fire("Success", "Episodes added successfully.", "success");
    } catch (error) {
      console.error("Error uploading episodes:", error);
      // Swal.fire("Error", error.response.data.message, "error");
    } finally {
      setUploading(false);
    }
  };
  const handleEditClick = (podcast) => {
    setSelectedAlbum(podcast);
    setSongs([]);
    setShowEditDialog(true);
  };

  useEffect(() => {
    getArtistIdS();
  }, []);

  const getArtistIdS = async () => {
    console.log("Function called-- ");
    try {
      const response = await httpClient.get(`artist/misc/get-artist`);
      console.log("Raw Response:", response);
      const artistIds = response.data?.data.artist?._id;
      console.log("artistIds", artistIds);
      setArtistIds(artistIds);

      if (response.data.success) {
        // console.log("Artist get data -", data);
      } else {
        console.log("Data success false hai:", response.data.message);
      }
    } catch (error) {
      console.error("Catch Block Error:", error);
    }
  };
  const handleAddSong = () => {
    setSongs((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        title: "",
        description: "",
        releaseDate: "",
        themes: [],
        coverImage: null,
        file: null,
      },
    ]);
  };

  const handleRemoveSong = (index) => {
    setSongs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSongChange = (index, field, value) => {
    setSongs((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  const handleSongFileChange = (index, field, file) => {
    setSongs((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: file || null } : s)),
    );
  };

  // API: Delete podcast
  const handleDeletePodcast = async (podcastId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    try {
      const response = await httpClient.delete(
        `admin/media-collection/delete/${podcastId}`,
      );

      if (response.data.success) {
        await fetchPodcasts();
      } else {
        console.error("Failed to delete podcast:", response.data.message);
      }
    } catch (error) {
      console.error("Error deleting podcast:", error);
    }
  };

  const handleAddEpisode = (e) => {
    e.preventDefault();

    if (!episodeForm.title.trim()) {
      alert("Please enter episode title");
      return;
    }

    const newEpisode = {
      id: Date.now().toString(), // Temporary ID for local state
      title: episodeForm.title,
      duration: episodeForm.duration || "00:00",
      description: episodeForm.description,
      audioFile: episodeForm.audioFile,
      createdAt: new Date().toISOString(),
    };

    setEditFormData((prev) => ({
      ...prev,
      episodes: [...prev.episodes, newEpisode],
    }));

    resetEpisodeForm();
  };

  const handleDeleteSong = async (episode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    try {
      await httpClient.delete(`admin/media-track/delete/${episode._id}`);

      setViewingPodcast((prev) => {
        const updatedEpisodes =
          prev.details?.episodes?.filter((ep) => ep._id !== episode._id) ||
          prev.episodes?.filter((ep) => ep._id !== episode._id) ||
          [];

        if (prev.details?.episodes) {
          return {
            ...prev,
            details: {
              ...prev.details,
              episodes: updatedEpisodes,
            },
          };
        } else {
          return {
            ...prev,
            episodes: updatedEpisodes,
          };
        }
      });
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleDeleteEpisode = async (episodeId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this song?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    setEditFormData((prev) => ({
      ...prev,
      episodes: prev.episodes.filter((episode) => episode.id !== episodeId),
    }));
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const openEditModal = async (podcast) => {
    console.log("Podcast object:", podcast);
    console.log("Podcast ID:", podcast.id);

    let podcastWithDetails = { ...podcast };
    try {
      const details = await fetchPodcastDetails(podcast.id);
      if (details) {
        podcastWithDetails = {
          ...podcast,
          fileUrl: details.fileUrl || podcast.fileUrl,
          coverImageUrl: details.coverImageUrl || podcast.coverImageUrl,
        };
      }
    } catch (_) {
      /* use list data only */
    }
    setEditingPodcast(podcastWithDetails);
    setEditFormData({
      title: podcast.title,
      description: podcast.description,
      artist: podcast.artist,
      genre: podcast.genre || "",
      releaseDate: podcast.releaseDate || "",
      episodes: Array.isArray(podcast.episodes) ? podcast.episodes : [],
    });
    setIsEditModalOpen(true);
    resetEpisodeForm();
    setEditCoverImageFile(null);
    setEditPodcastAudioFile(null);
    setEditFormErrors({});
    setUploadFormErrors({ coverImage: "", file: "" });
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingPodcast(null);
    setEditCoverImageFile(null);
    setEditPodcastAudioFile(null);
    setEditFormErrors({});
    setUploadFormErrors({ coverImage: "", file: "" });
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setViewingPodcast(null);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPodcasts(1, searchQuery);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchPodcasts(newPage, searchQuery);
  };

  // Get current page podcasts
  const getCurrentPagePodcasts = () => {
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    return podcasts.slice(startIndex, endIndex);
  };
  console.log("podcasts", podcasts);
  const currentPagePodcasts = getCurrentPagePodcasts();
  const pendingPodcasts = podcasts.filter((p) => p.status === "pending");
  const approvedPodcasts = podcasts.filter((p) => p.status === "approved");
  const mostPlayed = approvedPodcasts.reduce(
    (prev, current) =>
      (prev.playCount || 0) > (current.playCount || 0) ? prev : current,
    approvedPodcasts[0] || {},
  );

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Close modal when clicking outside or pressing Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        if (isModalOpen) closeModal();
        if (isEditModalOpen) closeEditModal();
        if (isViewModalOpen) closeViewModal();
        if (showEditDialog) {
          setShowEditDialog(false);
          setSongs([]);
          setSelectedAlbum(null);
        }
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isModalOpen, isEditModalOpen, isViewModalOpen, showEditDialog]);
  console.log("editingPodcast", editingPodcast);
  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__items">
              <Link to="/artist/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              Podcast
            </li>
          </ul>
        </div>

        <div className="col-12">
          <div className="main__title main__title--page d-flex justify-content-between align-items-center">
            <h2 className="card-title text-capitalize mt-3">
              Podcast Management
            </h2>
            <button
              className="btn btn-primary"
              onClick={openModal}
              disabled={loading}
            >
              {loading ? "Loading..." : "Add Podcast"}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="col-12 mt-4">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSearch} className="d-flex gap-3">
                <div className="flex-grow-1 position-relative">
                  <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                  <input
                    type="text"
                    className="form-control ps-5"
                    placeholder="Search podcasts by artist name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  Search
                </button>
                {searchQuery && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setSearchQuery("");
                      setCurrentPage(1);
                      fetchPodcasts(1);
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Most Played Podcast */}
        <div className="col-12 mt-4">
          <div className="card bg-gradient-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Most Played Podcast</h5>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <div className="bg-white bg-opacity-20 p-3 rounded-circle">
                    <i className="bi bi-play-fill"></i>
                  </div>
                  <div>
                    <h4 className="mb-0">
                      {mostPlayed?.title || "No podcasts"}
                    </h4>
                    <p className="mb-0 opacity-75">{mostPlayed?.artist}</p>
                  </div>
                </div>
                <div className="text-end">
                  <h2 className="mb-0">
                    {(mostPlayed?.playCount || 0).toLocaleString()}
                  </h2>
                  <p className="mb-0 opacity-75">plays</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* All Podcasts */}
        <div className="col-12 mt-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">All Podcasts</h5>
              <span className="text-muted">
                Total: {podcasts.length} podcasts
              </span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading podcasts...</p>
                </div>
              ) : currentPagePodcasts.length > 0 ? (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Cover</th>
                          <th>Title</th>
                          {/* <th>Artist</th> */}
                          <th>Release Date</th>
                          <th>Duration</th>
                          <th>Status</th>
                          <th>Plays</th>
                          <th>Episodes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPagePodcasts.map((podcast) => (
                          <tr key={podcast.id}>
                            <td>
                              <img
                                src={
                                  podcast.coverImageUrl ||
                                  "https://via.placeholder.com/50"
                                }
                                alt={podcast.title}
                                className="rounded"
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.src =
                                    "https://via.placeholder.com/50";
                                }}
                              />
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {podcast.title.length > 50
                                    ? podcast.title.substring(0, 50) + "..."
                                    : podcast.title}
                                </strong>
                                <br />
                                <small className="text-muted">
                                  {podcast.description.substring(0, 50)}...
                                </small>
                              </div>
                            </td>
                            {/* <td>{podcast.artist}</td> */}
                            <td>{podcast.releaseDate}</td>
                            <td>{formatDuration(podcast.duration)}</td>
                            <td>
                              <span
                                className={`badge ${
                                  podcast.status === "approved"
                                    ? "bg-success"
                                    : podcast.status === "pending"
                                      ? "bg-warning"
                                      : "bg-danger"
                                }`}
                              >
                                {podcast.status || "pending"}
                              </span>
                            </td>
                            <td>{(podcast.playCount || 0).toLocaleString()}</td>
                            <td>
                              <span className="badge bg-secondary">
                                {podcast.numberOfItems || 0} episodes
                              </span>
                            </td>
                            <td>
                              <div className="btn-group">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleViewPodcast(podcast)}
                                  title="View Details"
                                >
                                  <i
                                    className="fa fa-eye"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => openEditModal(podcast)}
                                  title="Edit Podcast"
                                >
                                  <i
                                    className="fa fa-pencil"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleEditClick(podcast)}
                                  title="Add Episodes"
                                >
                                  <i
                                    className="fa fa-plus"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() =>
                                    handleDeletePodcast(podcast.id)
                                  }
                                  title="Delete Podcast"
                                >
                                  <i
                                    className="fa fa-trash"
                                    aria-hidden="true"
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="bi bi-chevron-left"></i> Previous
                        </button>

                        {getPageNumbers().map((page) => (
                          <button
                            key={page}
                            className={`btn btn-sm ${
                              currentPage === page
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ))}

                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next <i className="bi bi-chevron-right"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-muted py-4">
                  No podcasts found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Podcast Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">Add New Podcast</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeModal}
                disabled={submitting}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Title */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="title">
                      Podcast Title *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.title ? "is-invalid" : ""}`}
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter album title"
                    />
                    {formErrors.title && (
                      <div className="text-danger small mt-1">
                        {formErrors.title}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="description">
                      Description *
                    </label>
                    <textarea
                      className={`form-control ${formErrors.description ? "is-invalid" : ""}`}
                      id="description"
                      name="description"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Enter album description"
                    />
                    {formErrors.description && (
                      <div className="text-danger small mt-1">
                        {formErrors.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Artist Name */}
                {/* <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="artist">
                      Artist Name *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.artist ? "is-invalid" : ""}`}
                      id="artist"
                      name="artist"
                      value={formData.artist}
                      onChange={handleInputChange}
                      placeholder="Enter artist name"
                    />
                    <div className="form-text">
                      Enter the name of the artist
                    </div>
                    {formErrors.artist && (
                      <div className="text-danger small mt-1">
                        {formErrors.artist}
                      </div>
                    )}
                  </div>
                </div> */}

                {/* Cover Image */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="coverImage">
                      Podcast Cover Image *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${uploadFormErrors.coverImage ? "is-invalid" : ""}`}
                      id="coverImage"
                      name="coverImage"
                      accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setCoverImageFile(null);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isImage =
                          ["image/jpeg", "image/png", "image/jpg"].includes(
                            type,
                          ) || ["jpg", "jpeg", "png"].includes(ext);
                        if (isImage) {
                          setCoverImageFile(file);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage: "",
                          }));
                        } else {
                          setCoverImageFile(null);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            coverImage:
                              "Please choose an image file (JPEG or PNG).",
                          }));
                        }
                      }}
                    />
                    {uploadFormErrors.coverImage && (
                      <div className="text-danger small mt-1">
                        {uploadFormErrors.coverImage}
                      </div>
                    )}
                  </div>
                </div>

                {/* File Upload */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="file">
                      Podcast File *
                    </label>
                    <input
                      type="file"
                      className={`form-control ${uploadFormErrors.file ? "is-invalid" : ""}`}
                      id="file"
                      name="file"
                      accept="audio/mpeg, audio/wav, .mp3, .wav"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) {
                          setPodcastAudioFile(null);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                          return;
                        }
                        const ext = (file.name || "")
                          .split(".")
                          .pop()
                          ?.toLowerCase();
                        const type = (file.type || "").toLowerCase();
                        const isAudio =
                          [
                            "audio/mpeg",
                            "audio/wav",
                            "audio/mp3",
                            "audio/x-wav",
                          ].includes(type) || ["mp3", "wav"].includes(ext);
                        if (isAudio) {
                          setPodcastAudioFile(file);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            file: "",
                          }));
                        } else {
                          setPodcastAudioFile(null);
                          setUploadFormErrors((prev) => ({
                            ...prev,
                            file: "Please choose an audio file (MP3 or WAV).",
                          }));
                        }
                      }}
                    />
                    <div className="form-text">
                      Upload the album file (audio/mpeg, audio/wav, .mp3, .wav)
                    </div>
                    {uploadFormErrors.file && (
                      <div className="text-danger small mt-1">
                        {uploadFormErrors.file}
                      </div>
                    )}
                  </div>
                </div>

                {/* Genres */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="genre">
                      Podcast Genres
                    </label>
                    <select
                      name="genre"
                      className={`form-control ${formErrors.genre ? "is-invalid" : ""}`}
                      value={formData.genre}
                      onChange={handleInputChange}
                      disabled={loadingGenres}
                    >
                      <option value="">
                        {loadingGenres ? "Loading genres..." : "Select Genre"}
                      </option>
                      {genres.map((g) => (
                        <option key={g._id} value={g._id}>
                          {g.name}
                        </option>
                      ))}
                      {genres.length === 0 && !loadingGenres && (
                        <option value="" disabled>
                          No genres available
                        </option>
                      )}
                    </select>
                    <div className="form-text">Select a Genre (required)</div>
                    {formErrors.genre && (
                      <div className="text-danger small mt-1">
                        {formErrors.genre}
                      </div>
                    )}
                  </div>
                </div>

                {/* Release Date */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="releaseDate">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${formErrors.releaseDate ? "is-invalid" : ""}`}
                      id="releaseDate"
                      name="releaseDate"
                      value={formData.releaseDate}
                      onChange={handleInputChange}
                    />
                    {formErrors.releaseDate && (
                      <div className="text-danger small mt-1">
                        {formErrors.releaseDate}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Adding Podcast..." : "Add Podcast"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Podcast Modal with Episodes Management */}
      {isEditModalOpen && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div
            className="modal-content modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title">Edit Podcast </h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeEditModal}
                disabled={submitting}
              ></button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {/* Title */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editTitle">
                      Podcast Title *
                    </label>
                    <input
                      type="text"
                      className={`form-control ${editFormErrors.title ? "is-invalid" : ""}`}
                      id="editTitle"
                      name="title"
                      value={editFormData.title}
                      onChange={handleEditInputChange}
                      placeholder="Enter album title"
                    />
                    {editFormErrors.title && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.title}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editDescription">
                      Description *
                    </label>
                    <textarea
                      className={`form-control ${editFormErrors.description ? "is-invalid" : ""}`}
                      id="editDescription"
                      name="description"
                      rows={3}
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      placeholder="Enter album description"
                    />
                    {editFormErrors.description && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Genres */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editGenre">
                      Podcast Genres *
                    </label>
                    <select
                      name="genre"
                      className={`form-control ${editFormErrors.genre ? "is-invalid" : ""}`}
                      value={editFormData.genre}
                      onChange={handleEditInputChange}
                      disabled={loadingGenres}
                    >
                      <option value="">
                        {loadingGenres ? "Loading genres..." : "Select Genre"}
                      </option>
                      {genres.map((g) => (
                        <option key={g._id} value={g._id}>
                          {g.name}
                        </option>
                      ))}
                      {genres.length === 0 && !loadingGenres && (
                        <option value="" disabled>
                          No genres available
                        </option>
                      )}
                    </select>
                    {/* <div className="form-text">Select a Genre (required)</div> */}
                    {editFormErrors.genre && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.genre}
                      </div>
                    )}
                  </div>
                </div>

                {/* Release Date */}
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label" htmlFor="editReleaseDate">
                      Release Date *
                    </label>
                    <input
                      type="date"
                      className={`form-control ${editFormErrors.releaseDate ? "is-invalid" : ""}`}
                      id="editReleaseDate"
                      name="releaseDate"
                      value={editFormData.releaseDate}
                      onChange={handleEditInputChange}
                    />
                    {editFormErrors.releaseDate && (
                      <div className="text-danger small mt-1">
                        {editFormErrors.releaseDate}
                      </div>
                    )}
                  </div>
                </div>
                {/* Current Cover Image & Audio (from API) */}

                <div style={{ display: "flex" }}>
                  {/* Cover Image */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="editCoverImage">
                        Podcast Cover Image *
                      </label>
                      <input
                        type="file"
                        className={`form-control ${uploadFormErrors.coverImage ? "is-invalid" : ""}`}
                        id="editCoverImage"
                        name="coverImage"
                        accept="image/jpeg, image/png, .jpg, .jpeg, .png"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) {
                            setEditCoverImageFile(null);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              coverImage: "",
                            }));
                            return;
                          }
                          const ext = (file.name || "")
                            .split(".")
                            .pop()
                            ?.toLowerCase();
                          const type = (file.type || "").toLowerCase();
                          const isImage =
                            ["image/jpeg", "image/png", "image/jpg"].includes(
                              type,
                            ) || ["jpg", "jpeg", "png"].includes(ext);
                          if (isImage) {
                            setEditCoverImageFile(file);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              coverImage: "",
                            }));
                          } else {
                            setEditCoverImageFile(null);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              coverImage:
                                "Please choose an image file (JPEG or PNG).",
                            }));
                          }
                        }}
                      />
                      {uploadFormErrors.coverImage && (
                        <div className="text-danger small mt-1">
                          {uploadFormErrors.coverImage}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* File Upload (optional on edit) */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label" htmlFor="editFile">
                        Podcast File *
                      </label>
                      <input
                        type="file"
                        className={`form-control ${uploadFormErrors.file ? "is-invalid" : ""}`}
                        id="editFile"
                        name="file"
                        accept="audio/mpeg, audio/wav, .mp3, .wav"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) {
                            setEditPodcastAudioFile(null);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              file: "",
                            }));
                            return;
                          }
                          const ext = (file.name || "")
                            .split(".")
                            .pop()
                            ?.toLowerCase();
                          const type = (file.type || "").toLowerCase();
                          const isAudio =
                            [
                              "audio/mpeg",
                              "audio/wav",
                              "audio/mp3",
                              "audio/x-wav",
                            ].includes(type) || ["mp3", "wav"].includes(ext);
                          if (isAudio) {
                            setEditPodcastAudioFile(file);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              file: "",
                            }));
                          } else {
                            setEditPodcastAudioFile(null);
                            setUploadFormErrors((prev) => ({
                              ...prev,
                              file: "Please choose an audio file (MP3 or WAV).",
                            }));
                          }
                        }}
                      />
                      <div className="form-text">
                        Upload a new file to replace (optional)
                      </div>
                      {uploadFormErrors.file && (
                        <div className="text-danger small mt-1">
                          {uploadFormErrors.file}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(editingPodcast?.coverImageUrl || editingPodcast?.fileUrl) && (
                  <div className="col-12 mb-3">
                    <div className="row g-3">
                      {editingPodcast.coverImageUrl && (
                        <div className="col-md-6">
                          <label className="form-label d-block">
                            Current Cover Image *
                          </label>
                          <img
                            src={editingPodcast.coverImageUrl}
                            alt="Current cover"
                            className="img-fluid rounded border"
                            style={{ maxHeight: "120px", objectFit: "cover" }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/300";
                            }}
                          />
                        </div>
                      )}
                      {editingPodcast.fileUrl && (
                        <div className="col-md-6">
                          <label className="form-label d-block">
                            Current Podcast File
                          </label>
                          <audio
                            controls
                            src={editingPodcast.fileUrl}
                            className="w-100"
                            style={{ maxHeight: "48px" }}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeEditModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    submitting ||
                    !!uploadFormErrors.coverImage ||
                    !!uploadFormErrors.file
                  }
                >
                  {submitting ? "Updating..." : "Update Podcast"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Podcast Details Modal */}
      {isViewModalOpen && viewingPodcast && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div
            className="modal-content modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h5 className="modal-title">Podcast Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeViewModal}
                disabled={loadingPodcastDetails}
              ></button>
            </div>

            <div className="modal-body">
              {loadingPodcastDetails ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                      Loading podcast details...
                    </span>
                  </div>
                  <p className="mt-2">Loading podcast details...</p>
                </div>
              ) : (
                <div className="row">
                  {/* Podcast Cover Image */}
                  <div className="col-md-4 text-center mb-4">
                    <img
                      src={
                        viewingPodcast.coverImageUrl ||
                        "https://via.placeholder.com/300"
                      }
                      alt={viewingPodcast.title}
                      className="img-fluid rounded shadow"
                      style={{ maxHeight: "300px", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/300";
                      }}
                    />
                  </div>

                  {/* Podcast Details */}
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-12 mb-3">
                        <h4 className="text-primary">
                          {" "}
                          {viewingPodcast.title.length > 30
                            ? viewingPodcast.title.substring(0, 30) + "..."
                            : viewingPodcast.title}
                        </h4>
                        <p className="text-muted">
                          {viewingPodcast.description}
                        </p>
                      </div>

                      {/* <div className="col-md-6 mb-3">
                        <strong>Artist:</strong>
                        <p className="mb-0">{viewingPodcast.artist}</p>
                      </div> */}

                      <div className="col-md-6 mb-3">
                        <strong>Genre:</strong>
                        <p className="mb-0">
                          {(() => {
                            const g = viewingPodcast.genre;
                            if (!g) return "—";
                            if (Array.isArray(g)) {
                              const names = g
                                .map(
                                  (id) =>
                                    genres.find((x) => x._id === id)?.name,
                                )
                                .filter(Boolean);
                              return names.length ? names.join(", ") : "—";
                            }
                            return genres.find((x) => x._id === g)?.name ?? g;
                          })()}
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <strong>Release Date:</strong>
                        <p className="mb-0">{viewingPodcast.releaseDate}</p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <strong>Status:</strong>
                        <span
                          className={`badge ${
                            viewingPodcast.status === "approved"
                              ? "bg-success"
                              : viewingPodcast.status === "pending"
                                ? "bg-warning"
                                : "bg-danger"
                          }`}
                        >
                          {viewingPodcast.status || "pending"}
                        </span>
                      </div>

                      <div className="col-md-6 mb-3">
                        <strong>Total Plays:</strong>
                        <p className="mb-0">
                          {(viewingPodcast.playCount || 0).toLocaleString()}
                        </p>
                      </div>

                      <div className="col-md-6 mb-3">
                        <strong>Duration:</strong>
                        <p className="mb-0">
                          {formatDuration(viewingPodcast.duration)}
                        </p>
                      </div>

                      {/* Additional details from API response */}
                      {viewingPodcast.details && (
                        <>
                          <div className="col-12 mb-3">
                            <strong>Content Type:</strong>
                            <p className="mb-0">
                              {viewingPodcast.details.contentType || "podcast"}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Episodes Section */}
                    <div className="row mt-4">
                      <div className="col-12">
                        <h5 className="border-bottom pb-2">
                          Episodes (
                          {
                            (
                              viewingPodcast.details?.episodes ??
                              (Array.isArray(viewingPodcast.episodes)
                                ? viewingPodcast.episodes
                                : [])
                            ).length
                          }
                          )
                        </h5>

                        {(() => {
                          const episodes =
                            viewingPodcast.details?.episodes ??
                            (Array.isArray(viewingPodcast.episodes)
                              ? viewingPodcast.episodes
                              : []) ??
                            [];

                          return episodes.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-sm table-striped">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Title</th>
                                    <th>Duration</th>
                                    <th>Description</th>
                                    <th>Play</th>
                                    <th>Plays</th>
                                    <th>Created</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {episodes.map((episode, index) => (
                                    <tr
                                      key={episode._id || episode.id || index}
                                    >
                                      <td>{index + 1}</td>
                                      <td>
                                        <strong>{episode.title}</strong>
                                      </td>
                                      <td>
                                        {typeof episode.duration === "number"
                                          ? formatDuration(episode.duration)
                                          : episode.duration || "N/A"}
                                      </td>
                                      <td>
                                        <small className="text-muted">
                                          {episode.description ||
                                            "No description"}
                                        </small>
                                      </td>
                                      <td>
                                        {episode.fileUrl ? (
                                          <audio
                                            controls
                                            src={episode.fileUrl}
                                            preload="none"
                                            style={{
                                              maxHeight: "32px",
                                              width: "100%",
                                              minWidth: "120px",
                                            }}
                                          >
                                            Your browser does not support audio.
                                          </audio>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                      <td>
                                        {(
                                          episode.playCount ??
                                          episode.plays ??
                                          0
                                        ).toLocaleString()}
                                      </td>
                                      <td>
                                        <small>
                                          {episode.createdAt
                                            ? new Date(
                                                episode.createdAt,
                                              ).toLocaleDateString()
                                            : "N/A"}
                                        </small>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => {
                                              setEditEpisode({
                                                _id: episode._id,
                                                title: episode.title,
                                                description:
                                                  episode.description,
                                                releaseDate:
                                                  episode.releaseDate.split(
                                                    "T",
                                                  )[0],
                                                artist: episode.artist,
                                                themes: episode.themes,
                                                coverImage: episode.coverImage,
                                                file: episode.file,
                                                fileUrl: episode.fileUrl,
                                                coverImageUrl:
                                                  episode.coverImageUrl,
                                                genre: episode?.genre,
                                              });
                                              setShowEditEpisodeDialog(true);
                                            }}
                                          >
                                            <i
                                              className="fa fa-pencil"
                                              aria-hidden="true"
                                            ></i>
                                          </button>
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() =>
                                              handleDeleteSong(episode)
                                            }
                                          >
                                            <i
                                              className="fa fa-trash"
                                              aria-hidden="true"
                                            ></i>
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center text-muted py-3">
                              <i className="bi bi-music-note-list display-4"></i>
                              <p className="mt-2">No episodes available</p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showEditDialog && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "800px" }}
          >
            <button
              className="modal__close"
              onClick={() => {
                setShowEditDialog(false);
                setSongs([]);
                setSelectedAlbum(null);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">
              Add Episode to Podcast: {selectedAlbum?.title}
            </h3>

            <div className="space-y-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Episode</h5>
                <button
                  onClick={handleAddSong}
                  className="btn btn-primary btn-sm"
                >
                  <i className="bi bi-plus"></i> Add Episode
                </button>
              </div>

              {songs.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No episode added yet. Click "Add Episode" to start.
                </div>
              ) : (
                <div className="space-y-3">
                  {songs.map((song, index) => (
                    <div className="card p-3" key={song.id}>
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="mb-0">Episode {index + 1}</h6>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          onClick={() => handleRemoveSong(index)}
                          title="Remove episode"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                          Remove
                        </button>
                      </div>

                      <div className="row g-3">
                        {/* Song Title */}
                        <div className="col-md-6">
                          <label className="form-label">Episode Title </label>
                          <input
                            type="text"
                            className={`form-control `}
                            value={song.title}
                            required
                            onChange={(e) =>
                              handleSongChange(index, "title", e.target.value)
                            }
                            placeholder="Enter song title"
                          />
                          {songErrors[index]?.title && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].title}
                            </div>
                          )}
                        </div>

                        {/* Description */}
                        <div className="col-md-6">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            required
                            value={song.description}
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "description",
                                e.target.value,
                              )
                            }
                            placeholder="Enter song description"
                          />
                          {songErrors[index]?.description && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].description}
                            </div>
                          )}
                        </div>

                        {/* Themes */}
                        <div className="col-md-6">
                          <label className="form-label">Episode Genres</label>
                          <select
                            className="form-control"
                            value=""
                            required
                            onChange={(e) => {
                              const val = e.target.value;
                              if (!val) return;
                              const currentThemes = song.themes || [];
                              if (currentThemes.includes(val)) return;
                              handleSongChange(index, "themes", [
                                ...currentThemes,
                                val,
                              ]);
                            }}
                          >
                            <option value="">Select Genre</option>
                            {genres.map((g) => (
                              <option key={g._id} value={g._id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                          {songErrors[index]?.themes && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].themes}
                            </div>
                          )}
                        </div>

                        {/* Release Date */}
                        <div className="col-md-6">
                          <label className="form-label">Release Date *</label>
                          <input
                            type="date"
                            className="form-control"
                            value={song.releaseDate}
                            required
                            onChange={(e) =>
                              handleSongChange(
                                index,
                                "releaseDate",
                                e.target.value,
                              )
                            }
                          />
                          {songErrors[index]?.releaseDate && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].releaseDate}
                            </div>
                          )}
                        </div>

                        {/* Cover Image */}
                        <div className="col-md-6">
                          <label className="form-label">Cover Image *</label>
                          <input
                            type="file"
                            className="form-control"
                            required
                            accept="image/*"
                            onChange={(e) =>
                              handleSongFileChange(
                                index,
                                "coverImage",
                                e.target.files?.[0],
                              )
                            }
                          />
                          {songErrors[index]?.coverImage && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].coverImage}
                            </div>
                          )}
                        </div>

                        {/* Audio File */}
                        <div className="col-md-6">
                          <label className="form-label">Audio File </label>
                          <input
                            type="file"
                            required
                            className={`form-control `}
                            accept="audio/*"
                            onChange={(e) =>
                              handleSongFileChange(
                                index,
                                "file",
                                e.target.files?.[0],
                              )
                            }
                          />
                          {songErrors[index]?.file && (
                            <div className="text-danger small mt-1">
                              {songErrors[index].file}
                            </div>
                          )}
                          {/* {!song.file && (
                            <div className="text-danger small mt-1">
                              Audio file is required.
                            </div>
                          )} */}
                        </div>
                      </div>

                      {/* Selected Themes */}
                      {song.themes && song.themes.length > 0 && (
                        <div className="mt-3">
                          <label className="form-label">Selected Genre:</label>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {song.themes.map((themeId) => {
                              const g = genres.find((t) => t._id === themeId);
                              return g ? (
                                <span
                                  key={themeId}
                                  className="badge bg-secondary"
                                >
                                  {g.name}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex gap-2 justify-content-end pt-4 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSongs([]);
                    setSelectedAlbum(null);
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitSongs}
                  className="btn btn-primary"
                  disabled={uploading || songs.length === 0}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Uploading...
                    </>
                  ) : (
                    `Upload ${songs.length} Episodes`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showEditEpisodeDialog && (
        <div className="modal-overlay">
          <div
            className="modal-content zoom-anim-dialog modal modal--form eventss"
            style={{ maxWidth: "800px" }}
          >
            <button
              className="modal__close"
              onClick={() => {
                setShowEditEpisodeDialog(false);
                // setSongs([]);
                // setSelectedAlbum(null);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z" />
              </svg>
            </button>

            <h3 className="sign__title border-bottom pb-3">Edit Episode</h3>

            <div className="space-y-4">
              {/* <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Episode</h5>
                <button
                  onClick={handleAddSong}
                  className="btn btn-primary btn-sm"
                >
                  <i className="bi bi-plus"></i> Add Episode
                </button>
              </div> */}

              {/* {songs.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  No episode added yet. Click "Add Episode" to start.
                </div>
              ) : ( */}
              <div className="space-y-3">
                {/* {songs.map((song, index) => ( */}
                <div className="card p-3">
                  {/* <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="mb-0">Episode {index + 1}</h6>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                          onClick={() => handleRemoveSong(index)}
                          title="Remove episode"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                          Remove
                        </button>
                      </div> */}

                  <div className="row g-3">
                    {/* Song Title */}
                    <div className="col-md-6">
                      <label className="form-label">Episode Title *</label>
                      <input
                        type="text"
                        className={`form-control ${!editEpisode.title?.trim() ? "is-invalid" : ""}`}
                        value={editEpisode.title}
                        onChange={(e) =>
                          setEditEpisode({
                            ...editEpisode,
                            title: e.target.value,
                          })
                        }
                        placeholder="Enter song title"
                      />
                      {editEpisodeErrors.title && (
                        <div className="text-danger small mt-1">
                          {editEpisodeErrors.title}
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div className="col-md-6">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editEpisode.description}
                        onChange={(e) =>
                          setEditEpisode({
                            ...editEpisode,
                            description: e.target.value,
                          })
                        }
                        placeholder="Enter song description"
                      />
                    </div>

                    {/* Themes */}
                    <div className="col-md-6">
                      <label className="form-label">Episode Genres *</label>

                      <select
                        className={`form-control ${
                          !editEpisode.genre || editEpisode.genre.length === 0
                            ? "is-invalid"
                            : ""
                        }`}
                        value=""
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) return;

                          const currentGenres = editEpisode.genre || [];
                          if (currentGenres.includes(val)) return;

                          setEditEpisode({
                            ...editEpisode,
                            genre: [...currentGenres, val],
                          });
                        }}
                      >
                        <option value="">Select Genre</option>
                        {genres.map((g) => (
                          <option key={g._id} value={g._id}>
                            {g.name}
                          </option>
                        ))}
                      </select>

                      {editEpisodeErrors.genre && (
                        <div className="text-danger small mt-1">
                          {editEpisodeErrors.genre}
                        </div>
                      )}
                    </div>

                    {/* Release Date */}
                    <div className="col-md-6">
                      <label className="form-label">Release Date *</label>
                      <input
                        type="date"
                        className={`form-control ${editEpisodeErrors.releaseDate ? "is-invalid" : ""}`}
                        value={editEpisode.releaseDate || ""}
                        onChange={(e) => {
                          setEditEpisode({
                            ...editEpisode,
                            releaseDate: e.target.value,
                          });
                          if (editEpisodeErrors.releaseDate) {
                            setEditEpisodeErrors((prev) => ({
                              ...prev,
                              releaseDate: null,
                            }));
                          }
                        }}
                      />
                      {editEpisodeErrors.releaseDate && (
                        <div className="text-danger small mt-1">
                          {editEpisodeErrors.releaseDate}
                        </div>
                      )}
                    </div>

                    {/* Cover Image */}
                    <div className="col-md-6">
                      <label className="form-label">Cover Image *</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={
                          (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setEditEpisode({
                              ...editEpisode,
                              coverImage: e.target.files?.[0],
                              coverImageUrl: URL.createObjectURL(file),
                            });
                          }
                          // handleSongFileChange(
                          //   index,
                          //   "coverImage",
                          //   e.target.files?.[0],
                          // )
                        }
                      />
                      {editEpisodeErrors.coverImage && (
                        <div className="text-danger small mt-1">
                          {editEpisodeErrors.coverImage}
                        </div>
                      )}
                      {editEpisode?.coverImageUrl && (
                        <div className="image-file mt-1">
                          <img
                            style={{ height: "60px" }}
                            src={editEpisode.coverImageUrl}
                            alt="Cover Image"
                          />
                          <small className="text-muted d-block mt-1">
                            Upload a new image above to replace
                          </small>
                        </div>
                      )}
                    </div>

                    {/* Audio File */}
                    <div className="col-md-6">
                      <label className="form-label">Audio File *</label>
                      <input
                        type="file"
                        className={`form-control ${!editEpisode.file && !editEpisode.fileUrl ? "is-invalid" : ""}`}
                        accept="audio/*"
                        onChange={
                          (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setEditEpisode({
                              ...editEpisode,
                              file: e.target.files?.[0],
                              fileUrl: URL.createObjectURL(file),
                            });
                          }
                          // handleSongFileChange(
                          //   index,
                          //   "file",
                          //   e.target.files?.[0],
                          // )
                        }
                      />
                      {editEpisodeErrors.file && (
                        <div className="text-danger small mt-1">
                          {editEpisodeErrors.file}
                        </div>
                      )}
                      {editEpisode.fileUrl && (
                        <div className="audio-file mt-1">
                          <audio src={editEpisode.fileUrl} controls />
                          <small className="text-muted d-block mt-1">
                            Upload a new file above to replace
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selected Genres */}
                  {editEpisode.genre && editEpisode.genre.length > 0 && (
                    <div className="mt-3">
                      <label className="form-label">Selected Genre:</label>
                      <div className="d-flex flex-wrap gap-2 mt-1">
                        {editEpisode.genre.map((genreId) => {
                          const g = genres.find((t) => t._id === genreId);
                          if (!g) return null;

                          return (
                            <span
                              key={genreId}
                              className="badge bg-secondary d-flex align-items-center gap-1"
                            >
                              {g.name}
                              <button
                                type="button"
                                className="btn btn-sm p-0 text-white"
                                style={{ lineHeight: 1 }}
                                onClick={() =>
                                  setEditEpisode((prev) => ({
                                    ...prev,
                                    genre: prev.genre.filter(
                                      (id) => id !== genreId,
                                    ),
                                  }))
                                }
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {/* ))} */}
              </div>
              {/* )} */}

              <div className="d-flex gap-2 justify-content-end pt-4 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowEditEpisodeDialog(false);
                    // setSongs([]);
                    // setSelectedAlbum(null);
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmitSongs}
                  className="btn btn-primary"
                  disabled={
                    uploading
                    // ||
                    // songs.length === 0 ||
                    // !songs.every((s) => (s.title || "").trim() && s.file)
                  }
                >
                  {uploading ? "Saving..." : `Save Episode`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: modalSlideIn 0.3s ease-out;
        }

        .modal-lg {
          max-width: 800px;
        }

        .modal-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-title {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-footer {
          padding: 1rem 1.5rem;
          border-top: 1px solid #dee2e6;
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }

        .btn-close {
          background: none;
          border: none;
          font-size: 1.25rem;
          opacity: 0.7;
          cursor: pointer;
          padding: 0;
          width: 1.5rem;
          height: 1.5rem;
        }

        .btn-close:hover {
          opacity: 1;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .badge {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        .table-responsive {
          border-radius: 0.375rem;
        }

        .table th {
          border-top: none;
          font-weight: 600;
          color: #495057;
          background-color: #f8f9fa;
        }
      `}</style>
    </div>
  );
}

export default Podcast;
